/**
 * The daily car: every day races a different era, caricatured in the same toy,
 * poster-palette style. Everyone drives the same car on a given day. Physics
 * overrides and track scale for each era live in tuning.ts; this file holds
 * the look and the sound.
 */
export const ERA_IDS = ['1932', '1937', '1954', '1961', '1972', '1985', '1994', '2004', '2022'] as const;
export type EraId = (typeof ERA_IDS)[number];

export type BodyKind = 'monoposto' | 'streamliner' | 'frontEngine' | 'rearEngine' | 'wedge' | 'turbo' | 'highNose' | 'v10' | 'groundEffect';
export type HelmetKind = 'leather' | 'cloth' | 'fullface';
export type WheelKind = 'wire' | 'disc' | 'classic' | 'slick' | 'grooved' | 'covered';
/** Palette colour names (render/palette.ts). */
export type Paint = 'red' | 'cream' | 'ochre' | 'grass' | 'terracotta' | 'olive' | 'cypress' | 'asphalt' | 'brown' | 'silver';

export interface EraLook {
  body: BodyKind;
  wheels: WheelKind;
  main: Paint;
  accent: Paint;
  ghostMain: Paint;
  ghostAccent: Paint;
  helmet: HelmetKind;
  helmetColor: Paint;
  helmetStripe: Paint;
  scarf: boolean;
  /** Defaults to red, like the poster. */
  scarfColor?: Paint;
  /** Wheel rim paint for slicks; defaults to cream. */
  rim?: Paint;
  number: string;
}

export interface EngineSound {
  /** Firing pulses per crankshaft revolution (cylinders / 2 for a four-stroke). */
  firingPerRev: number;
  /** Supercharger whine: frequency = rpm/60 * ratio. */
  whine: { ratio: number; gain: number } | null;
  turbo: boolean;
  /** The blow-off hiss when you lift (turbo cars; defaults on). */
  blowoff?: boolean;
  /** Soft-clip amount: rougher engines growl. */
  grit: number;
  /** How far the tone filter opens. */
  brightness: number;
}

export interface Era {
  id: EraId;
  year: number;
  /** Italian name shown on the title card. */
  name: string;
  /** One line about how it drives. */
  blurb: string;
  look: EraLook;
  sound: EngineSound;
}

export const ERAS: Record<EraId, Era> = {
  '1932': {
    id: '1932',
    year: 1932,
    name: 'The Single-Seater',
    blurb: 'Supercharged straight-eight on skinny tyres. Brake early and let it drift.',
    look: { body: 'monoposto', wheels: 'wire', main: 'red', accent: 'brown', ghostMain: 'cypress', ghostAccent: 'cream', helmet: 'leather', helmetColor: 'brown', helmetStripe: 'brown', scarf: true, number: '2' },
    sound: { firingPerRev: 4, whine: { ratio: 5.5, gain: 0.05 }, turbo: false, grit: 2.2, brightness: 0.55 },
  },
  '1937': {
    id: '1937',
    year: 1937,
    name: 'The Silver Arrow',
    blurb: 'A supercharged V12 in a silver dart. Blistering on the straights, light on grip.',
    look: { body: 'streamliner', wheels: 'disc', main: 'silver', accent: 'red', ghostMain: 'cypress', ghostAccent: 'cream', helmet: 'cloth', helmetColor: 'cream', helmetStripe: 'cream', scarf: true, number: '8' },
    sound: { firingPerRev: 6, whine: { ratio: 7, gain: 0.06 }, turbo: false, grit: 2.8, brightness: 0.7 },
  },
  '1954': {
    id: '1954',
    year: 1954,
    name: 'The Cigar',
    blurb: 'Front-engined and poised. Smooth hands, big sweeping lines.',
    look: { body: 'frontEngine', wheels: 'wire', main: 'red', accent: 'cream', ghostMain: 'olive', ghostAccent: 'cream', helmet: 'cloth', helmetColor: 'cream', helmetStripe: 'cream', scarf: true, number: '12' },
    sound: { firingPerRev: 3, whine: null, turbo: false, grit: 3, brightness: 0.7 },
  },
  '1961': {
    id: '1961',
    year: 1961,
    name: 'The Sharknose',
    blurb: 'The little rear-engined red racer. Nimble, friendly, eager.',
    look: { body: 'rearEngine', wheels: 'classic', main: 'red', accent: 'cream', ghostMain: 'cypress', ghostAccent: 'cream', helmet: 'cloth', helmetColor: 'cream', helmetStripe: 'cream', scarf: true, number: '7' },
    sound: { firingPerRev: 4, whine: null, turbo: false, grit: 3.2, brightness: 1 },
  },
  '1972': {
    id: '1972',
    year: 1972,
    name: 'The Wedge',
    blurb: 'Wedge, wings and fat slicks. The downforce lets you carry speed.',
    look: { body: 'wedge', wheels: 'slick', main: 'brown', accent: 'ochre', ghostMain: 'red', ghostAccent: 'cream', helmet: 'fullface', helmetColor: 'cream', helmetStripe: 'red', scarf: false, rim: 'ochre', number: '5' },
    sound: { firingPerRev: 4, whine: null, turbo: false, grit: 4, brightness: 1.1 },
  },
  '1985': {
    id: '1985',
    year: 1985,
    name: 'The Turbo',
    blurb: 'A turbo four with a kick. Wait for the boost, then hold on.',
    look: { body: 'turbo', wheels: 'slick', main: 'red', accent: 'cream', ghostMain: 'cypress', ghostAccent: 'ochre', helmet: 'fullface', helmetColor: 'ochre', helmetStripe: 'brown', scarf: false, rim: 'cream', number: '1' },
    sound: { firingPerRev: 2, whine: null, turbo: true, grit: 3.5, brightness: 1.05 },
  },
  '1994': {
    id: '1994',
    year: 1994,
    name: 'The High Nose',
    blurb: 'High nose, screaming V10, grip for days. Late brakes, early throttle.',
    look: { body: 'highNose', wheels: 'slick', main: 'cypress', accent: 'ochre', ghostMain: 'red', ghostAccent: 'cream', helmet: 'fullface', helmetColor: 'cream', helmetStripe: 'cypress', scarf: false, rim: 'cream', number: '6' },
    sound: { firingPerRev: 5, whine: null, turbo: false, grit: 2.4, brightness: 1.25 },
  },
  '2004': {
    id: '2004',
    year: 2004,
    name: 'The Red One',
    blurb: 'Peak V10: light, sharp and brutally quick. The most agile car of all.',
    look: { body: 'v10', wheels: 'grooved', main: 'red', accent: 'cream', ghostMain: 'cypress', ghostAccent: 'cream', helmet: 'fullface', helmetColor: 'ochre', helmetStripe: 'red', scarf: false, rim: 'asphalt', number: '3' },
    sound: { firingPerRev: 5, whine: null, turbo: false, grit: 2.1, brightness: 1.4 },
  },
  '2022': {
    id: '2022',
    year: 2022,
    name: 'The Ground Effect',
    blurb: 'The most grip of any car: sharp in the slow corners, glued down in the fast ones.',
    look: { body: 'groundEffect', wheels: 'covered', main: 'terracotta', accent: 'brown', ghostMain: 'cypress', ghostAccent: 'cream', helmet: 'fullface', helmetColor: 'cream', helmetStripe: 'terracotta', scarf: false, rim: 'brown', number: '4' },
    // Turbo-hybrid V6: the MGU-H whistle rides the revs, and no blow-off.
    sound: { firingPerRev: 3, whine: { ratio: 11, gain: 0.018 }, turbo: true, blowoff: false, grit: 2.4, brightness: 0.85 },
  },
};

export function isEraId(v: string | null | undefined): v is EraId {
  return !!v && (ERA_IDS as readonly string[]).includes(v);
}
